/**
 * Slack's two fields (user, 18/10/2026: "one channel by default, and we
 * paste a thread — everything in one channel"): the channel's webhook,
 * shared by every country (a new one moves them all), and this country's
 * thread — required, so each country's alerts stay together.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { INPUT_CLASS } from '../../../editor/general/fields/field-chrome';
import { HowToGet } from '../howto/HowToGet';
import type { ChannelDraft } from '../notification-draft';

const LABEL = 'flex flex-col gap-1 text-[11px] font-medium text-muted-foreground';
const FIELD = `${INPUT_CLASS} h-8 w-full font-mono text-xs font-normal text-foreground`;

interface Props {
  draft: ChannelDraft;
  countryName: string;
  disabled: boolean;
  onChange: (patch: Partial<ChannelDraft>) => void;
}

/** True when Slack is switched on but this country has no thread yet. */
export const missingThread = (d: ChannelDraft): boolean => d.enabled && !d.hasThread && d.threadLink.trim() === '';

export const SlackFields: FC<Props> = ({ draft, countryName, disabled, onChange }) => (
  <div className="flex flex-col gap-2">
    <label className={LABEL}>
      Channel — shared by every country
      <input
        type="text"
        value={draft.target}
        disabled={disabled}
        placeholder={draft.hasTarget ? 'keep the shared channel' : 'https://hooks.slack.com/services/…'}
        onChange={(e) => onChange({ target: e.target.value })}
        className={FIELD}
        aria-label="Slack webhook"
        title="The incoming webhook of the one channel every country posts into"
      />
      <span className="font-normal">Every country posts into this one channel. A new webhook moves every country to it.</span>
    </label>
    <label className={LABEL}>
      {countryName}&apos;s thread — required
      <input
        type="text"
        value={draft.threadLink}
        disabled={disabled}
        placeholder={draft.hasThread ? 'keep the saved thread' : 'https://….slack.com/archives/…/p…'}
        onChange={(e) => onChange({ threadLink: e.target.value })}
        className={FIELD}
        aria-label="Slack thread link"
        title={`Copy link on ${countryName}'s message in the channel`}
      />
      {missingThread(draft) ? (
        <span className="font-medium text-warning">
          Paste the link of {countryName}&apos;s message in the channel — its alerts reply there, so every country keeps its own thread.
        </span>
      ) : null}
    </label>
    <HowToGet channel="SLACK" />
  </div>
);
